import { FC, useRef, useState, useCallback, useMemo, useEffect } from "react"
import classNames from "classnames"
import { useSelector, useDispatch } from "react-redux"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"

// components
import { Icon } from "../../Icon/Icon"
import { SearchResultsBox } from "../SearchResultsBox/SearchResultsBox"

// redux
import { useGetHelpArticlesByTitleQuery } from "../../../services/help_articles.service"
import { setSearchQuery as setReduxSearchQuery } from "../../../store/slices/search_query.slice"

// icons
import { CloseIcon, MagnifyingGlassIcon } from "@eventbrite/marmalade"

// utils
import { useDebounce } from "../../../utils/hooks/debounce.hook"
import { renderRichText } from "../../../utils/contentful/render_rich_text"
import { navigateWithPrefix } from "../../../utils/general_utils"

// types
import { RootState } from "../../../store/store"

// styles
import * as styles from "./SearchBar.module.scss"

const {
  container,
  searchIconContainerDesktop,
  searchIconContainerMobile,
  searchTextContainer,
  searchInputHeader,
  searchInputHomepage,
  crossIconContainer,
  searchResultsContainer,
  searchTakoverActive,
  isLoggedIn,
  takeoverActive,
  takeoverInactive
} = styles

export type Props = {
  takeover: { enabled: true; toggleTakeover: () => void } | { enabled: false }
  messageSearchBarMicrocopy: Queries.HomepageDataQuery["messageSearchBar"]
  headerSearchResultsMicrocopy: Queries.HomepageDataQuery["headerSearchResults"]
  disableInput?: boolean
  isSearchTakeoverOpen?: boolean
  placeholderText?: string
  wrapperClassName?: string
  isResultsBoxShown?: boolean
  headerSearch?: boolean
}

export const SearchBar: FC<Props> = ({
  messageSearchBarMicrocopy,
  headerSearchResultsMicrocopy,
  wrapperClassName,
  placeholderText,
  disableInput,
  isSearchTakeoverOpen,
  takeover,
  isResultsBoxShown = true,
  headerSearch = false
}) => {
  const placeholderCopy = useMemo<string>(() => {
    if (
      messageSearchBarMicrocopy?.microcopyValue?.raw &&
      messageSearchBarMicrocopy?.microcopyValue?.raw?.length > 0
    ) {
      return messageSearchBarMicrocopy?.microcopyValue?.raw?.length > 0
        ? documentToPlainTextString(
          JSON.parse(messageSearchBarMicrocopy?.microcopyValue?.raw)
        )
        : ""
    }

    // fallback text
    return "Search help articles"
  }, [messageSearchBarMicrocopy])

  const dispatch = useDispatch()

  const currentlyLoggedIn = useSelector((state: RootState) => state.userProfile)
    ? true
    : false
  const searchQuery = useSelector((state: RootState) => state.searchQuery.query)
  const langCode = useSelector((state: RootState) => state?.metaData?.langCode)

  const setSearchQuery = useCallback((q: string) => {
    dispatch(setReduxSearchQuery(q))
  }, [])

  const ref = useRef<HTMLInputElement>(null)
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false)

  const debouncedSearchQuery = useDebounce(searchQuery, 50)

  const showCrossIcon = useMemo(
    () => isSearchTakeoverOpen || searchQuery?.length > 0,
    [isSearchTakeoverOpen, searchQuery]
  )
  const toggleTakeover = useMemo(() => {
    if (takeover?.enabled) {
      return takeover.toggleTakeover
    }

    return () => undefined
  }, [takeover])

  useEffect(() => {
    setSearchQuery("")

    if (ref && isSearchTakeoverOpen) {
      focusInput()
    }
  }, [isSearchTakeoverOpen])

  useEffect(() => {
    setSearchQuery(debouncedSearchQuery)
  }, [debouncedSearchQuery])

  // on first load we want to check for query parameters and set that to the current search query in the SearchBar
  useEffect(() => {
    const queryParams =
      typeof window !== "undefined"
        ? new URLSearchParams(window?.location?.search)
        : null

    if (queryParams) {
      const q = queryParams.get("q")

      if (q && q.length > 0 && searchQuery !== q && !searchQuery) {
        setSearchQuery(q)
      }
    }
  }, [])

  const data =
    typeof window !== "undefined"
      ? useGetHelpArticlesByTitleQuery(
        {
          query: debouncedSearchQuery
        },
        {
          skip: debouncedSearchQuery?.length < 2
        }
      )
      : undefined

  // handlers
  const focusInput = useCallback(() => {
    if (ref?.current?.focus) {
      ref.current.focus()
    }

    setIsSearchMenuOpen(true)
  }, [ref, setIsSearchMenuOpen])

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setSearchQuery(e.target.value || "")
    },
    [searchQuery, setSearchQuery]
  )
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        if (typeof searchQuery !== "undefined") {
          navigateWithPrefix(`/${langCode}/search`)
        }
      },
      [searchQuery, setSearchQuery]
    )
  const handleFocus: React.FocusEventHandler<HTMLInputElement> =
    useCallback(() => {
      setIsSearchMenuOpen(true)
    }, [isSearchMenuOpen, setIsSearchMenuOpen])

  const handleBlur: React.FocusEventHandler<HTMLInputElement> =
    useCallback(() => {
      setTimeout(() => {
        setIsSearchMenuOpen(false)
      }, 250)
    }, [isSearchMenuOpen, setIsSearchMenuOpen])

  const handleCrossIconClick = useCallback(() => {
    if (searchQuery?.length > 0) {
      setSearchQuery("")
    }

    if (takeover?.enabled && isSearchTakeoverOpen) {
      toggleTakeover()
    }
  }, [searchQuery, setSearchQuery, isSearchTakeoverOpen, toggleTakeover])

  const handleMagnifierClick = useCallback(() => {
    if (searchQuery?.length > 0) {
      focusInput()
      // Get the current URL
      const url = new URL(window?.location?.href)

      // Parse the query parameters from the URL
      const queryParams = url?.searchParams
      if (
        searchQuery?.length > 0
        // searchQuery?.toLowerCase() !== queryParams?.get("q")?.toLowerCase()
      ) {
        queryParams.set("q", searchQuery)

        navigateWithPrefix(`/${langCode}/search?${queryParams?.toString()}`, {
          replace: true
        })
      }
    } else {
      navigateWithPrefix(`/${langCode}/search`)
    }
  }, [langCode, searchQuery, setSearchQuery])

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      if (typeof window !== "undefined") {
        e.preventDefault()
        const queryParams =
          typeof window !== "undefined"
            ? new URLSearchParams(window?.location?.search)
            : null

        data?.refetch()

        navigateWithPrefix(
          `/${langCode}/search${queryParams?.toString()?.length > 0
            ? `?${queryParams?.toString()}`
            : ""
          }`
        )
      }
    },
    []
  )

  const searchInputClass = classNames({
    [searchInputHeader]: headerSearch,
    [searchInputHomepage]: !headerSearch,
    [takeoverActive]: headerSearch && isSearchTakeoverOpen,
    [takeoverInactive]: headerSearch && !isSearchTakeoverOpen
  })

  return (
    <form
      className={classNames(container, wrapperClassName, {
        [searchTakoverActive]: isSearchTakeoverOpen,
        [isLoggedIn]: currentlyLoggedIn
      })}
      onSubmit={handleSubmit}
    >
      <div className={searchTextContainer}>
        {/** Desktop search icon */}
        <Icon
          iconProps={{
            onClick: handleMagnifierClick
          }}
          className={searchIconContainerDesktop}
          icon={<MagnifyingGlassIcon size="small" />}
        />

        {/** mobile search icon needs to have a different click handler */}
        <Icon
          id="mobileMagnifier"
          iconProps={{
            onClick: () => {
              if (takeover?.enabled && !isSearchTakeoverOpen) {
                toggleTakeover()
              }
            }
          }}
          className={classNames(searchIconContainerMobile)}
          icon={<MagnifyingGlassIcon size="small" />}
        />

        <input
          id="searchBar"
          role="searchbox"
          ref={ref}
          type="text"
          className={searchInputClass}
          value={searchQuery}
          placeholder={placeholderText ? placeholderText : placeholderCopy}
          onChange={handleChange}
          disabled={disableInput === true ? true : false}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-labelledby="searchBarLabel"
          onKeyDown={(event) => {
            if (
              takeover?.enabled &&
              isSearchTakeoverOpen &&
              event.key === "Escape"
            ) {
              toggleTakeover()
            } else if (event.key === "Enter") {
              handleKeyDown(event)
            }
          }}
        />

        {showCrossIcon && (
          <span>
            <Icon
              iconProps={{
                onClick: handleCrossIconClick
              }}
              className={crossIconContainer}
              icon={<CloseIcon size="small" />}
            />
          </span>
        )}

        {isSearchMenuOpen && isResultsBoxShown && searchQuery?.length > 1 && (
          <div className={searchResultsContainer}>
            <SearchResultsBox
              hits={data?.data?.result?.hit}
              query={searchQuery}
              isSearchMenuOpen={isSearchMenuOpen}
              headerSearchResultsMicrocopy={headerSearchResultsMicrocopy}
            />
          </div>
        )}
      </div>
    </form>
  )
}
